import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockPrescriptionTemplates } from "./mock/prescriptionTemplates";
import type { PageResponse } from "../types/patient";
import type {
  PrescriptionTemplateListFilter,
  PrescriptionTemplateResponse,
  PrescriptionTemplateSaveRequest,
  PrescriptionTemplateSummaryResponse,
} from "../types/prescriptionTemplate";

function buildParams(
  filter: PrescriptionTemplateListFilter | undefined,
  pageable: { page?: number; size?: number; sort?: string }
) {
  const p: Record<string, string | number | boolean> = {};
  if (filter?.q) p.q = filter.q;
  if (filter?.icd10Code) p.icd10Code = filter.icd10Code;
  if (filter?.visibleOnly != null) p.visibleOnly = filter.visibleOnly;
  if (pageable.page != null) p.page = pageable.page;
  if (pageable.size != null) p.size = pageable.size;
  if (pageable.sort) p.sort = pageable.sort;
  return p;
}

const realPrescriptionTemplatesApi = {
  list(filter: PrescriptionTemplateListFilter | undefined,
       pageable: { page?: number; size?: number; sort?: string }) {
    return apiClient
      .get<PageResponse<PrescriptionTemplateSummaryResponse>>("/v1/prescription-templates", {
        params: buildParams(filter, pageable),
      })
      .then((r) => r.data);
  },
  get(id: string) {
    return apiClient
      .get<PrescriptionTemplateResponse>(`/v1/prescription-templates/${id}`)
      .then((r) => r.data);
  },
  create(req: PrescriptionTemplateSaveRequest) {
    return apiClient
      .post<PrescriptionTemplateResponse>("/v1/prescription-templates", req)
      .then((r) => r.data);
  },
  update(id: string, req: PrescriptionTemplateSaveRequest) {
    return apiClient
      .put<PrescriptionTemplateResponse>(`/v1/prescription-templates/${id}`, req)
      .then((r) => r.data);
  },
  duplicate(id: string, newCode: string) {
    return apiClient
      .post<PrescriptionTemplateResponse>(
        `/v1/prescription-templates/${id}/duplicate`, null,
        { params: { code: newCode } })
      .then((r) => r.data);
  },
  show(id: string) {
    return apiClient.post<void>(`/v1/prescription-templates/${id}/show`).then(() => undefined);
  },
  hide(id: string) {
    return apiClient.post<void>(`/v1/prescription-templates/${id}/hide`).then(() => undefined);
  },
  softDelete(id: string) {
    return apiClient.delete<void>(`/v1/prescription-templates/${id}`).then(() => undefined);
  },
  restore(id: string) {
    return apiClient.post<void>(`/v1/prescription-templates/${id}/restore`).then(() => undefined);
  },
};

// Routes to the mock adapter until the real `/api/v1/prescription-templates`
// is wired end-to-end (auth + tenant). Flip via `VITE_USE_MOCKS=false`.
export const prescriptionTemplatesApi = MOCKS_ENABLED
  ? mockPrescriptionTemplates
  : realPrescriptionTemplatesApi;

export const prescriptionTemplateQueryKeys = {
  all: ["prescription-templates"] as const,
  list: (filter: PrescriptionTemplateListFilter | undefined, page: number) =>
    ["prescription-templates", "list", filter ?? {}, page] as const,
  detail: (id: string) => ["prescription-templates", "detail", id] as const,
};
